import { useCallback, useEffect, useRef, useState } from 'react';
import { cognitionApi } from '../services/cognitionApi';
import { GSCORE_DASHBOARD_STUB } from '../fixtures/gscoreStub';

// Backend V1 stub is live for GET /api/v1/cognition/dashboard.
const USE_STUB = false;

export function useGScoreDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Only the most recent request may write state — `reload` can be pressed
  // while the previous request is still in flight, and the loser of that race
  // must not overwrite the winner's result. Same guard as useGScoreHistory.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const current = () => requestId === requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      if (USE_STUB) {
        await new Promise((r) => setTimeout(r, 120));
        if (!current()) return;
        setData(GSCORE_DASHBOARD_STUB);
      } else {
        const res = await cognitionApi.fetchDashboard();
        if (!current()) return;
        setData(res);
      }
    } catch (e) {
      if (!current()) return;
      // Backend messages can carry internal detail (session ids, ownership
      // assertions). Log them for developers; show the user fixed copy. Same
      // class of leak fixed in useStudentDetail.js / useSharedCourse.js.
      if (process.env.NODE_ENV !== 'production') {
        console.error('[useGScoreDashboard] load error:', e);
      }
      setError('Something went wrong loading your G-Score. Please try again.');
    } finally {
      if (current()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  return { data, loading, error, reload: load };
}
